import { Dropdown, Flex, Space, Typography } from "antd";
import { Toggle } from "@libertify/ui";
import { useTranslate } from "hooks/useTranslate/";
import { useTemplateEditorStore } from "../../storyboardEditor/store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../storyboardEditor/store/useTemplatesStatusStore";
import { useTemplateData } from "../../storyboardEditor/hooks/useTemplateData";
import { PanelSection } from "../../storyboardEditor/components/StoryboardMenuTabs/PanelSection";
import { ThemeApplyToAllButton } from "./ThemeApplyToAllButton";
import { ItemAlignmentFeature } from "../../storyboardEditor/components/features/ItemAlignmentFeature";
import { Logo } from "../../storyboardEditor/types";
import { useCallback, useMemo, useState } from "react";
import { loadImageRatio } from "../../storyboardEditor/helpers";
import { DEFAULT_LOGO_RELATIVE_HEIGHT } from "../../storyboardEditor/constants";
import { useChangeLayer } from "../../storyboardEditor/hooks/useChangeLayer";
import { useThemeData } from "../hooks/useThemeData";
import { SmallBrandKitPreview } from "../../SmallBrandKitPreview";
import { EditButton } from "../../storyboardEditor/components/controls/EditButton";

export const ThemeBrandingTab = ({
  previewWidth,
  previewHeight,
}: {
  previewWidth: number;
  previewHeight: number;
}) => {
  const t = useTranslate();
  const {
    brandKits,
    logo: brandKitLogo,
    activeBrandKit,
    setBrandKit,
    mediaAspectRatio,
  } = useThemeData();

  const [open, setOpen] = useState(false);

  const { logo, updateLogo } = useTemplateEditorStore((state) => ({
    logo: state.templateState.logo,
    updateLogo: state.updateLogo,
  }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const { placeholderAssets } = useTemplateData();
  const { newHighestLayerIndex } = useChangeLayer(
    logo.showLogo ? logo : undefined
  );

  const applyLogoFromAsset = useCallback(
    async (logoAsset: { asset_id: string; path: string }) => {
      const logoPath =
        placeholderAssets?.[logoAsset.asset_id] ?? logoAsset.path;
      const logoRatio = await loadImageRatio(logoPath);
      let logoHeight = DEFAULT_LOGO_RELATIVE_HEIGHT;
      let logoWidth = (logoHeight * logoRatio) / mediaAspectRatio;
      if (logoWidth > 1) {
        logoWidth = 1;
        logoHeight = mediaAspectRatio / logoRatio;
      }
      const logoY = 0.01;
      const logoX = 0.5 - logoWidth / 2;

      updateLogo({
        showLogo: true,
        assetId: logoAsset.asset_id,
        path: logoAsset.path,
        size: { height: logoHeight, width: logoWidth },
        position: { x: logoX, y: logoY },
        layerIndex: newHighestLayerIndex,
      });
      addTemplateUpdate();
    },
    [
      updateLogo,
      addTemplateUpdate,
      mediaAspectRatio,
      newHighestLayerIndex,
      placeholderAssets,
    ]
  );

  const onSwitchLogoDisplay = (checked: boolean) => {
    if (!logo.assetId && brandKitLogo) {
      if (checked) {
        applyLogoFromAsset(brandKitLogo).catch((error) => {
          console.error("Error applying logo from asset", error);
        });
      }
    } else {
      updateLogo({ showLogo: checked });
      addTemplateUpdate();
    }
  };

  const dropdownItems = useMemo(
    () =>
      brandKits.map((brandKit) => ({
        key: brandKit.id,
        label: <SmallBrandKitPreview brandKit={brandKit} compact />,
        onClick: () => {
          setBrandKit(brandKit);
          setOpen(false);
        },
      })),
    [brandKits, setBrandKit]
  );

  if (!activeBrandKit) return null;

  if (!brandKitLogo) {
    return (
      <PanelSection
        title={t("components.templates.settings.brandingTab.noLogo.title")}
        subtitle={t(
          "components.templates.settings.brandingTab.noLogo.description"
        )}
        primary
      />
    );
  }

  return (
    <>
      <PanelSection
        title={t("components.templates.settings.brandingTab.title")}
        subtitle={t(
          "components.templates.settings.brandingTab.themeDescription"
        )}
        primary
      />
      <PanelSection
        title={t("components.templates.settings.brandTab.brandKit")}
        noBorder
      >
        <Dropdown
          menu={{
            items: dropdownItems,
            selectedKeys: [activeBrandKit.id],
            style: { maxHeight: "50vh", overflowY: "auto" },
          }}
          trigger={["click"]}
          open={open}
          onOpenChange={setOpen}
          overlayStyle={{ maxWidth: "275px" }}
        >
          <Flex vertical gap={8}>
            <SmallBrandKitPreview brandKit={activeBrandKit} />
            <EditButton item="brandkit" onClick={() => setOpen(true)} />
          </Flex>
        </Dropdown>
      </PanelSection>

      <PanelSection
        title={t("components.templates.settings.brandingTab.logo")}
        noBorder
      >
        <Space align="center">
          <Toggle checked={logo.showLogo} onChange={onSwitchLogoDisplay} />
          <Typography.Text>
            {t("components.templates.settings.brandingTab.showLogo")}
          </Typography.Text>
        </Space>
      </PanelSection>
      {logo.showLogo && (
        <>
          <PanelSection
            title={t("components.templates.settings.brandingTab.logoAlignment")}
            noBorder
          >
            <ItemAlignmentFeature
              pickedItem={logo}
              onChange={(value) => {
                updateLogo(value as Partial<Logo>);
                addTemplateUpdate();
              }}
              previewWidth={previewWidth}
              previewHeight={previewHeight}
            />
          </PanelSection>
          <PanelSection>
            <ThemeApplyToAllButton
              partialTemplateState={{ logo }}
              filter={(t) => t.logo.showLogo}
              elementName={t("media.themeEditor.elementLogo")}
            />
          </PanelSection>
        </>
      )}
    </>
  );
};
